import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Grid2, InputAdornment, Typography } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { addDays } from "date-fns";
import format from "date-fns/format";
import React from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../../../../utils/date-locale";

const BORDER_COLOR = "#E5E5E5";

// Constants for layout
const SIDEBAR_WIDTH = "210px"; // Width for the sidebar (if any)

export interface ReviewScheduleDialogCalendarHeaderProps {
  searchedTerm: string;
  startingDay: Date;
  setSearchTerm: (term: string) => void;
}

export const ReviewScheduleDialogCalendarHeader = ({
  searchedTerm,
  startingDay,
  setSearchTerm,
}: ReviewScheduleDialogCalendarHeaderProps) => {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
  return (
    <Grid2
      container
      width={"100%"}
      sx={{
        position: "sticky",
        top: 0,
        height: "64px",
        minHeight: "64px",
        zIndex: 1000,
        backgroundColor: "rgba(255,255,255)",
        boxShadow: "0px 2px 4px -2px rgba(0, 0, 0, 0.2)",
        flexWrap: "nowrap",
        overflow: "hidden",
      }}
    >
      {/* Sidebar space reservation */}

      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderTop: `1px solid ${BORDER_COLOR}`,
          // borderBottom: `1px solid ${BORDER_COLOR}`,
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <InputBase
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
          endAdornment={
            searchedTerm.length > 0 && (
              <InputAdornment
                position="end"
                onClick={() => setSearchTerm("")}
                sx={{
                  cursor: "pointer",
                }}
              >
                <CloseIcon />
              </InputAdornment>
            )
          }
          placeholder={t("full-time-shifts_search-workers")}
          onFocus={(e) => e.target.select()}
          value={searchedTerm}
          onChange={(e) => setSearchTerm(e.target.value.replace(/^ +/, ""))}
          sx={{
            width: "100%",
            height: "100%",
            padding: "0 12px",
            border: "none",
            outline: "none",
          }}
          slotProps={{
            input: {
              style: {
                fontFamily: "Roboto",
                fontSize: "16px",
                lineHeight: "28px",
                fontWeight: 400,
                color: "rgba(0,0,0,0.87)",
                marginLeft: "3px",
              },
            },
          }}
        />
      </Box>
      {/* Scope of Movement */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          height: "100%",
          position: "relative",
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderTop: `1px solid ${BORDER_COLOR}`,
          minWidth: "187px",
          maxWidth: "187px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "24px",
              fontWeight: 400,
              fontFamily: "Roboto",
              color: "rgba(0,0,0,0.87)",
            }}
          >
            {t("full-time-shifts_scope-of-movement")}
          </Typography>
        </Box>
      </Box>
      {/* Days container */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          height: "100%",
          width: "100%",
          zIndex: 1000,
        }}
      >
        {startingDay &&
          Array.from({ length: 7 }).map((_, index) => {
            const day = addDays(startingDay, index);
            return (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    textAlign: "center",
                    height: "100%",
                    position: "relative",
                    paddingLeft: "0.8rem",
                    borderRight: `1px solid ${BORDER_COLOR}`,
                    borderTop: `1px solid ${BORDER_COLOR}`,
                    minWidth: "161px",
                    width: "161px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        lineHeight: "28px",
                        fontWeight: 400,
                        fontFamily: "Roboto",
                        color: "rgba(0,0,0,0.6)",
                        marginRight: "0.5rem",
                      }}
                    >
                      {format(day, "EEE", { locale: dateLocale })}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "24px",
                        lineHeight: "32px",
                        fontWeight: 400,
                        fontFamily: "Roboto",
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      {format(day, "d")}
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
      </Box>
    </Grid2>
  );
};
